import { useEffect, useState } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';

/** A single transient message. Mirrors the kernel `ui` slice `Toast` shape. */
export interface ToastItem {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  /** Auto-dismiss delay in ms; falls back to the Toaster's `defaultDuration`. */
  duration?: number;
}

export interface ToasterProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
  /** Default auto-dismiss delay (ms) when a toast doesn't set its own. */
  defaultDuration?: number;
}

interface ToneStyle {
  /** Tinted background + accent colour for the leading icon chip. */
  chip: string;
  /** Left accent border colour class. */
  accent: string;
  /** Inline SVG path(s) for the tone icon. */
  paths: string[];
}

const TONES: Record<ToastItem['type'], ToneStyle> = {
  success: {
    chip: 'bg-[var(--success-bg)] text-[var(--success)]',
    accent: 'border-l-[var(--success)]',
    paths: ['M20 6 9 17l-5-5'],
  },
  error: {
    chip: 'bg-[var(--danger-bg)] text-[var(--danger)]',
    accent: 'border-l-[var(--danger)]',
    paths: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
  },
  warning: {
    chip: 'bg-[var(--warning-bg)] text-[var(--warning)]',
    accent: 'border-l-[var(--warning)]',
    paths: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
  },
  info: {
    chip: 'bg-[var(--info-bg)] text-[var(--info)]',
    accent: 'border-l-[var(--info)]',
    paths: ['M12 16v-4', 'M12 8h.01', 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z'],
  },
};

function ToneIcon({ paths }: { paths: string[] }) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

function ToastCard({
  toast,
  onDismiss,
  defaultDuration,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
  defaultDuration: number;
}) {
  const tone = TONES[toast.type];
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Next frame → run the enter transition (translate/opacity) from its resting state.
    const raf = requestAnimationFrame(() => setShown(true));
    const timer = setTimeout(() => onDismiss(toast.id), toast.duration ?? defaultDuration);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [toast.id, toast.duration, defaultDuration, onDismiss]);

  return (
    <div
      role="alert"
      className={cn(
        'pointer-events-auto flex w-full max-w-[380px] items-start gap-3 rounded-[12px] border border-l-[3px] border-[var(--border)] bg-[var(--surface)] p-3 pr-2.5 shadow-[var(--shadow-3)] transition-all duration-200 ease-out',
        tone.accent,
        shown ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      )}
    >
      <span className={cn('mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full', tone.chip)}>
        <ToneIcon paths={tone.paths} />
      </span>
      <p className="min-w-0 flex-1 pt-0.5 text-[13.5px] font-medium leading-snug text-[var(--text)]">{toast.message}</p>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(toast.id)}
        className="-mr-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] text-[var(--text-3)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
      >
        <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 6 6 18" />
          <path d="M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Toaster — the app's transient-feedback viewport (snackbar). Presentational:
 * it renders whatever `toasts` it's given and calls `onDismiss` on close/expiry.
 * The clinic shell connects it to the Redux `ui` slice via `<ToastHost>`.
 *
 * Positioned bottom-right on desktop; on mobile it sits centred above the docked
 * bottom nav. `role="alert"` per toast announces errors to assistive tech.
 */
export function Toaster({ toasts, onDismiss, defaultDuration = 4000 }: ToasterProps) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[200] flex flex-col items-center gap-2 px-4 bottom-[calc(env(safe-area-inset-bottom,0px)+88px)] sm:inset-x-auto sm:right-4 sm:items-end sm:bottom-4"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} defaultDuration={defaultDuration} />
      ))}
    </div>
  );
}
